"use client";

import { Trans } from "@lingui/react/macro";
import { AlertCircleIcon, RefreshCwIcon } from "lucide-react";
import Link from "next/link";
import { type ReactNode, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import type { ChatAppError } from "@/lib/ai/chat-app-error";
import type { ChatQuotaStatusSnapshot } from "@/lib/ai/chat-quota-status";
import type { AiModelAccess } from "@/lib/ai/model-access";
import { AVAILABLE_MODELS, type ModelId } from "@/lib/ai/models";
import { cn } from "@/lib/utils";
import { AiModelUpgradeDialog } from "./ai-model-upgrade-dialog";
import {
  type ChatQuotaStatusCardAction,
  resolveChatQuotaStatusCardState,
} from "./chat-quota-view-state";

type Props = {
  appError: ChatAppError | null;
  currentModel: ModelId;
  aiModelAccess: AiModelAccess;
  lowQuotaStatus: ChatQuotaStatusSnapshot;
  highQuotaStatus: ChatQuotaStatusSnapshot;
  lang: string;
  workspaceId: string;
  onModelChange: (model: ModelId) => void;
  className?: string;
};

function resolveModelLabel(modelId: ModelId) {
  return AVAILABLE_MODELS.find(
    (availableModel) => availableModel.value === modelId,
  )?.label;
}

export function ChatQuotaStatusCard({
  appError,
  currentModel,
  aiModelAccess,
  lowQuotaStatus,
  highQuotaStatus,
  lang,
  workspaceId,
  onModelChange,
  className,
}: Props) {
  const [isUpgradeDialogOpen, setIsUpgradeDialogOpen] = useState(false);
  const state = useMemo(
    () =>
      resolveChatQuotaStatusCardState({
        appError,
        currentModel,
        aiModelAccess,
        lowQuotaStatus,
        highQuotaStatus,
      }),
    [appError, currentModel, aiModelAccess, lowQuotaStatus, highQuotaStatus],
  );

  const settingsHref = `/${lang}/${encodeURIComponent(workspaceId)}/settings#usage-limit`;

  if (state.kind === "hidden") {
    return null;
  }

  let message: ReactNode;
  let icon = <AlertCircleIcon className="h-4 w-4 text-foreground/70" />;
  let toneClassName = "border-border bg-background text-foreground";

  if (state.kind === "exceeded") {
    if (state.allAvailableTiersExceeded) {
      message = (
        <Trans>
          This month&apos;s AI limit has been reached. Try again next month.
        </Trans>
      );
    } else {
      message = (
        <Trans>This model has reached this month&apos;s AI limit.</Trans>
      );
    }
  } else if (state.kind === "plan-required") {
    message = <Trans>This workspace does not have an AI plan yet.</Trans>;
  } else if (state.kind === "restricted-model") {
    message = <Trans>This model is available on AI PRO and AI MAX.</Trans>;
  } else if (state.kind === "unavailable") {
    toneClassName = "border-border bg-muted/40 text-muted-foreground";
    icon = <RefreshCwIcon className="h-4 w-4 text-muted-foreground" />;
    message = state.message;
  } else {
    return null;
  }

  const renderPrimaryAction = (
    action: ChatQuotaStatusCardAction | undefined,
  ) => {
    if (!action) {
      return null;
    }

    if (action.kind === "upgrade") {
      return (
        <Button
          size="sm"
          variant="outline"
          className="h-7 rounded-lg border-border px-2.5 text-[11px] shadow-none"
          onClick={() => setIsUpgradeDialogOpen(true)}
        >
          <Trans>Upgrade AI plan</Trans>
        </Button>
      );
    }

    const targetLabel = resolveModelLabel(action.modelId);
    return (
      <Button
        size="sm"
        variant="outline"
        className="h-7 rounded-lg border-border px-2.5 text-[11px] shadow-none"
        onClick={() => onModelChange(action.modelId)}
      >
        <Trans>Switch to {targetLabel ?? action.modelId}</Trans>
      </Button>
    );
  };

  return (
    <>
      <div
        className={cn(
          "flex flex-col gap-2 rounded-2xl border px-3 py-2.5 shadow-none",
          toneClassName,
          className,
        )}
      >
        <div className="flex items-start gap-2">
          <div className="mt-0.5 shrink-0">{icon}</div>
          <div className="min-w-0 text-sm leading-5">{message}</div>
        </div>
        <div className="pl-6">
          <div className="flex flex-wrap items-center gap-1.5">
            {state.kind === "exceeded" ||
            state.kind === "plan-required" ||
            state.kind === "restricted-model"
              ? renderPrimaryAction(state.primaryAction)
              : null}
            {state.kind !== "unavailable" &&
            state.kind === "exceeded" &&
            state.showSettingsAction &&
            !state.primaryAction ? (
              <Button
                asChild
                size="sm"
                variant="outline"
                className="h-7 rounded-lg border-border px-2.5 text-[11px] shadow-none"
              >
                <Link href={settingsHref}>
                  <Trans>View AI limit</Trans>
                </Link>
              </Button>
            ) : null}
          </div>
        </div>
      </div>
      <AiModelUpgradeDialog
        open={isUpgradeDialogOpen}
        onOpenChange={setIsUpgradeDialogOpen}
        aiModelAccess={aiModelAccess}
      />
    </>
  );
}
